import React, { useState, useMemo } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

// Curated list of relevant countries (Switzerland-first + Portuguese, European, Americas, common).
// code = ISO 3166-1 alpha-2 (lower for flagcdn), dial = "+xx", name = display.
export const COUNTRIES = [
  { code: "ch", dial: "+41",  name: "Schweiz / Suisse / Svizzera" },
  { code: "de", dial: "+49",  name: "Deutschland" },
  { code: "fr", dial: "+33",  name: "France" },
  { code: "it", dial: "+39",  name: "Italia" },
  { code: "at", dial: "+43",  name: "Österreich" },
  { code: "li", dial: "+423", name: "Liechtenstein" },
  { code: "br", dial: "+55",  name: "Brasil" },
  { code: "pt", dial: "+351", name: "Portugal" },
  { code: "es", dial: "+34",  name: "España" },
  { code: "gb", dial: "+44",  name: "United Kingdom" },
  { code: "ie", dial: "+353", name: "Ireland" },
  { code: "nl", dial: "+31",  name: "Nederland" },
  { code: "be", dial: "+32",  name: "Belgique" },
  { code: "lu", dial: "+352", name: "Luxembourg" },
  { code: "pl", dial: "+48",  name: "Polska" },
  { code: "cz", dial: "+420", name: "Česko" },
  { code: "sk", dial: "+421", name: "Slovensko" },
  { code: "hu", dial: "+36",  name: "Magyarország" },
  { code: "se", dial: "+46",  name: "Sverige" },
  { code: "no", dial: "+47",  name: "Norge" },
  { code: "dk", dial: "+45",  name: "Danmark" },
  { code: "fi", dial: "+358", name: "Suomi" },
  { code: "is", dial: "+354", name: "Ísland" },
  { code: "gr", dial: "+30",  name: "Ελλάδα" },
  { code: "ro", dial: "+40",  name: "România" },
  { code: "bg", dial: "+359", name: "България" },
  { code: "hr", dial: "+385", name: "Hrvatska" },
  { code: "si", dial: "+386", name: "Slovenija" },
  { code: "tr", dial: "+90",  name: "Türkiye" },
  { code: "ru", dial: "+7",   name: "Россия" },
  { code: "ua", dial: "+380", name: "Україна" },
  { code: "us", dial: "+1",   name: "United States" },
  { code: "ca", dial: "+1",   name: "Canada" },
  { code: "mx", dial: "+52",  name: "México" },
  { code: "ar", dial: "+54",  name: "Argentina" },
  { code: "cl", dial: "+56",  name: "Chile" },
  { code: "uy", dial: "+598", name: "Uruguay" },
  { code: "py", dial: "+595", name: "Paraguay" },
  { code: "pe", dial: "+51",  name: "Perú" },
  { code: "co", dial: "+57",  name: "Colombia" },
  { code: "ve", dial: "+58",  name: "Venezuela" },
  { code: "ec", dial: "+593", name: "Ecuador" },
  { code: "bo", dial: "+591", name: "Bolivia" },
  { code: "il", dial: "+972", name: "ישראל" },
  { code: "ae", dial: "+971", name: "الإمارات" },
  { code: "sa", dial: "+966", name: "السعودية" },
  { code: "za", dial: "+27",  name: "South Africa" },
  { code: "au", dial: "+61",  name: "Australia" },
  { code: "nz", dial: "+64",  name: "New Zealand" },
  { code: "jp", dial: "+81",  name: "日本" },
  { code: "kr", dial: "+82",  name: "대한민국" },
  { code: "cn", dial: "+86",  name: "中国" },
  { code: "in", dial: "+91",  name: "India" },
];

const Flag = ({ code }) => (
  <img
    src={`https://flagcdn.com/w40/${code}.png`}
    srcSet={`https://flagcdn.com/w80/${code}.png 2x`}
    alt=""
    className="w-5 h-[15px] rounded-[2px] object-cover shadow-sm shrink-0"
    loading="lazy"
  />
);

export const PhoneField = ({ country, number, onCountryChange, onNumberChange, placeholder = "Telefon", wrapperClassName = "", triggerClassName = "", inputClassName = "" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return COUNTRIES;
    const q = query.toLowerCase();
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.includes(q)
    );
  }, [query]);

  return (
    <div className={`flex items-stretch gap-0 border-b border-white/25 focus-within:border-[#a7c084] transition-colors ${wrapperClassName}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="phone-country-trigger"
            className={`flex items-center gap-2 pr-3 py-3 font-sans text-sm hover:opacity-80 transition-opacity ${triggerClassName}`}
          >
            <Flag code={country.code} />
            <span className="tabular-nums opacity-70">{country.dial}</span>
            <ChevronDown size={14} strokeWidth={1.5} className="opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[300px] p-0 bg-background border-border" data-testid="phone-country-list">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
            <Search size={14} strokeWidth={1.5} className="text-muted-foreground shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  data-testid={`phone-country-${c.code}`}
                  onClick={() => {
                    onCountryChange(c);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary/60 transition-colors ${
                    c.code === country.code ? "bg-secondary/40" : ""
                  }`}
                >
                  <Flag code={c.code} />
                  <span className="flex-1 text-left truncate">{c.name}</span>
                  <span className="text-muted-foreground tabular-nums">{c.dial}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-xs text-muted-foreground">No match</li>
            )}
          </ul>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        inputMode="tel"
        placeholder={placeholder}
        value={number}
        onChange={(e) => onNumberChange(e.target.value.replace(/[^\d\s]/g, ""))}
        data-testid="phone-number-input"
        className={`flex-1 bg-transparent border-0 border-b-0 px-2 h-auto py-3 focus-visible:ring-0 focus-visible:border-0 rounded-none font-sans ${inputClassName}`}
      />
    </div>
  );
};

export const DEFAULT_COUNTRY = COUNTRIES[0]; // Switzerland
